const normalizePositiveInteger = (value, fallback = null) => {
  const normalized = Math.trunc(Number(value))
  return Number.isFinite(normalized) && normalized > 0 ? normalized : fallback
}

const normalizeOptionalText = (value) => {
  if (value === undefined || value === null) return ''
  return String(value).trim()
}

/**
 * Normalize an OpenAPI bigint identifier without passing through Number and losing precision.
 * Unsafe numeric inputs are rejected because their original integer can no longer be recovered.
 */
export const normalizeOrderConfirmEntityId = (value) => {
  if (typeof value === 'bigint') return value > 0n ? value.toString() : ''
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : ''
  }
  const raw = normalizeOptionalText(value)
  if (!/^\d+$/.test(raw)) return ''
  const normalized = raw.replace(/^0+(?=\d)/, '')
  return normalized === '0' ? '' : normalized
}

const normalizeComboItems = (raw) => {
  if (!Array.isArray(raw)) return []
  const items = []
  for (const row of raw) {
    const skuId = normalizeOrderConfirmEntityId(row?.skuId)
    if (!skuId) continue
    const item = {
      skuId,
      prodCount: normalizePositiveInteger(row?.prodCount ?? row?.basketCount, 1),
    }
    const comboItemType = normalizePositiveInteger(row?.comboItemType)
    if (comboItemType === 1 || comboItemType === 2) item.comboItemType = comboItemType
    items.push(item)
  }
  return items
}

/**
 * Keep the existing flat buyNowItem[] contract while carrying one complete combo envelope
 * on the leading row. The backend validates this envelope against the flat rows and derives
 * comboKey and item roles from the active promotion rules.
 */
export const attachCompleteComboEnvelope = (items) => {
  const rows = Array.isArray(items)
    ? items.map((item) => {
        const row = { ...(item || {}) }
        delete row.comboItems
        return row
      })
    : []
  if (!rows.length || ![4, 5].includes(Number(rows[0]?.promotionType))) return rows
  const comboItems = normalizeComboItems(rows)
  if (comboItems.length) rows[0].comboItems = comboItems
  return rows
}

const resolveComboBuyNowGroupKey = (row) => {
  const promotionType = Number(row?.promotionType)
  if (promotionType !== 4 && promotionType !== 5) return ''
  const promotionCode = normalizeOptionalText(row?.promotionCode ?? row?.activityCode)
  if (!promotionCode) return ''
  const comboKey = normalizeOptionalText(row?.comboKey ?? row?.combo_key)
  return `${promotionType}:${promotionCode}:${comboKey}`
}

const isRequiredComboBuyNowRow = (row) => {
  const required = row?.comboRequired ?? row?.required ?? row?.isRequired
  return (
    Number(row?.comboItemType) === 1 ||
    required === true ||
    required === 1 ||
    required === '1'
  )
}

export const hasSiblingSkuForSameProduct = (rows, groupIndexes, removeIndex, target) => {
  const targetProdId = normalizeOrderConfirmEntityId(
    target?.prodId ?? target?.productId ?? target?.productVO?.prodId
  )
  const targetSkuId = normalizeOrderConfirmEntityId(target?.skuId ?? target?.skuVO?.skuId)
  if (!targetProdId) return false
  return groupIndexes.some((index) => {
    if (index === removeIndex) return false
    const sibling = rows[index]
    const siblingProdId = normalizeOrderConfirmEntityId(
      sibling?.prodId ?? sibling?.productId ?? sibling?.productVO?.prodId
    )
    const siblingSkuId = normalizeOrderConfirmEntityId(sibling?.skuId ?? sibling?.skuVO?.skuId)
    return siblingProdId === targetProdId && siblingSkuId && siblingSkuId !== targetSkuId
  })
}

/**
 * Remove one editable buy-now line. A required product may contain multiple selected SKUs, so a
 * required/main SKU is removed independently while another SKU of the same product remains.
 * Removing the product's last required SKU removes the complete type-4 package instance.
 *
 * @param {Array<Object>} items
 * @param {number} removeIndex
 * @param {Object|null} targetMetadata - Confirm-page row metadata such as comboRequired.
 * @returns {{ items: Array<Object>, removed: boolean, removedWholeGroup: boolean }}
 */
export const removeOrderConfirmBuyNowItemAt = (items, removeIndex, targetMetadata = null) => {
  const rows = Array.isArray(items) ? items.map((row) => ({ ...(row || {}) })) : []
  if (!Number.isInteger(removeIndex) || removeIndex < 0 || removeIndex >= rows.length) {
    return { items: attachCompleteComboEnvelope(rows), removed: false, removedWholeGroup: false }
  }

  const target = rows[removeIndex]
  const groupKey = resolveComboBuyNowGroupKey(target)
  const groupIndexes = groupKey
    ? rows
        .map((row, index) => (resolveComboBuyNowGroupKey(row) === groupKey ? index : -1))
        .filter((index) => index >= 0)
    : []
  const targetWithMetadata = { ...target, ...(targetMetadata || {}) }
  const hasSameProductSibling = hasSiblingSkuForSameProduct(
    rows,
    groupIndexes,
    removeIndex,
    targetWithMetadata
  )
  const removeWholeGroup =
    Number(target?.promotionType) === 4 &&
    groupIndexes.length > 0 &&
    !hasSameProductSibling &&
    (removeIndex === groupIndexes[0] || isRequiredComboBuyNowRow(targetWithMetadata))
  const removedIndexes = removeWholeGroup ? new Set(groupIndexes) : new Set([removeIndex])
  const nextItems = rows.filter((_, index) => !removedIndexes.has(index))

  return {
    items: attachCompleteComboEnvelope(nextItems),
    removed: true,
    removedWholeGroup: removeWholeGroup,
  }
}

/**
 * @description 根据非致命 confirm 结果提交或回滚立即购买编辑快照
 * @param {Object} input
 * @param {Array<Object>} input.previousItems - 编辑前快照
 * @param {Array<Object>} input.editedItems - 编辑后快照
 * @param {boolean} input.confirmSucceeded - confirm 是否成功
 * @returns {Array<Object>}
 */
export const resolveOrderConfirmBuyNowEditSnapshot = ({
  previousItems = [],
  editedItems = [],
  confirmSucceeded = false,
} = {}) =>
  attachCompleteComboEnvelope(confirmSucceeded ? editedItems : previousItems)

/**
 * @description 将 session 中的多条立即购买原始数据规范为 V3 confirm 所需结构
 */
export const normalizeOrderConfirmBuyNowItems = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return []
  const out = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const prodId = normalizeOrderConfirmEntityId(row.prodId)
    const skuId = normalizeOrderConfirmEntityId(row.skuId)
    if (!prodId || !skuId) continue
    const prodCount = Math.min(
      9999,
      normalizePositiveInteger(row.prodCount ?? row.basketCount ?? row.quantity, 1)
    )
    const promotionTypeRaw = Number(row.promotionType)
    const promotionType =
      Number.isFinite(promotionTypeRaw) && promotionTypeRaw >= 0 ? promotionTypeRaw : 0
    const item = { prodId, skuId, prodCount, promotionType }

    const promotionCode = normalizeOptionalText(row.promotionCode ?? row.activityCode)
    if (promotionCode) item.promotionCode = promotionCode
    const comboKey = normalizeOptionalText(row.comboKey ?? row.combo_key)
    if (comboKey) item.comboKey = comboKey
    const comboItemType = normalizePositiveInteger(row.comboItemType)
    if (comboItemType) item.comboItemType = comboItemType
    const comboItems = normalizeComboItems(row.comboItems)
    if (comboItems.length) item.comboItems = comboItems
    out.push(item)
  }
  return out
}
